"use client"

import React from "react"
import Link from "next/link"
import { useParams, useRouter } from "next/navigation"
import { Icon } from "@/components/formatters/MXPIcon"
import isExternalLink from "../../utils/isExternalLink"
import { localizeUrl } from "@/lib/url/localizeUrl"
import {
  MXP_DESTINATION_ATTR,
  navigateHashLinkFromClick,
} from "@/lib/scrollToAnchor"
import { StoreBadge, detectStore } from "../StoreBadge/StoreBadge"
export interface TerraLink {
  id?: string /* Eventual Required ID. Likely Contentful ID */
  primary?: boolean /* primary vs secondary */
  size?: "small" | "medium" | "large"
  label?: string
  destination?: string
  downloadName?: string
  // Additional attributes
  tabIndex?: number
  dataDepth?: string
  dataName?: string
  dataHeading?: string
  ariaLabel?: string
  linkType?: "button" | "text"
  children?: React.ReactNode
  onClickHandler?: () => void
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>
  onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>
  onFocus?: React.FocusEventHandler<HTMLAnchorElement>
  onBlur?: React.FocusEventHandler<HTMLAnchorElement>
  className?: string
  isIcon?: boolean
  fill?: string
  width?: string
  height?: string
  openInSameTabExternalLink?: boolean
  // disabled?: boolean,
  disableStyles?: boolean
  excludeDefaultClasses?: boolean
}

/**
 * Primary UI component for user interaction
 */
export const TerraLink = ({
  primary = false,
  size = "medium",
  label,
  destination,
  downloadName,
  // Additional attributes
  tabIndex,
  dataDepth,
  dataName,
  dataHeading,
  ariaLabel,
  linkType = "button",
  children,
  onClickHandler,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  className,
  isIcon,
  openInSameTabExternalLink = false,
  // disabled = false
  // fill,
  // width,
  // height,
  disableStyles = false,
  excludeDefaultClasses = false,
}: TerraLink) => {
  const params = useParams()
  const router = useRouter()
  const lang = params?.lang as string
  const store = detectStore(destination)

  const getLocalizedHref = (href: string | undefined): string => {
    if (!href || !lang) return href || "#"
    return localizeUrl(href, lang)
  }

  const localizedHref = getLocalizedHref(destination)

  const handleClickCapture = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!localizedHref.includes("#")) return
    navigateHashLinkFromClick(event, localizedHref, href =>
      router.push(href, { scroll: false }),
    )
  }

  const updatedLinkType = store ? "text" : linkType

  // generate styles based on props passed
  const generateLinkStyles = () => {
    if (disableStyles) return ` ${className || ""}`

    if (excludeDefaultClasses) return className || ""

    if (updatedLinkType === "text") {
      return `t2-terra-text-link inline-flex ${className || ""}`
    }

    // Base: semantic class kept for terra.css / globals.css / layout.css parent-selector overrides
    const base =
      "t2-button rounded-full cursor-pointer inline-block font-bold text-center [text-decoration:none]! uppercase"

    const modeClasses = primary
      ? "t2-button--primary bg-yellow-m10 border-2 border-yellow-m10 text-gray-m9 transition-[color,background-color,border-color,box-shadow] duration-150 ease-in-out hover:bg-yellow-m10-hover hover:border-yellow-m10-hover"
      : "t2-button--secondary bg-transparent border border-gray-m9 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.15)] text-gray-m9 hover:border-gray-m9 hover:text-gray-m9 t2-dark:bg-transparent t2-dark:border-2 t2-dark:border-white t2-dark:shadow-none t2-dark:text-white"

    const sizeClassList =
      size === "small"
        ? "t2-button--small text-base tracking-[0.08em] leading-5 py-2.5 px-9"
        : size === "medium"
          ? "t2-button--medium text-lg tracking-[1.44px] leading-7 py-4 px-9 max-sm:text-base max-sm:py-1.5 max-sm:px-[34px]"
          : "t2-button--large text-lg tracking-[1.44px] leading-7 py-4 px-9 max-sm:text-base max-sm:py-1.5 max-sm:px-[34px]"

    return `${base} ${modeClasses} ${sizeClassList} ${className || ""}`
  }

  // Use Next.js Link for internal links, anchor tag for external links or downloads
  if (!downloadName && isExternalLink(destination) === false) {
    return (
      <Link
        className={generateLinkStyles()}
        href={localizedHref}
        scroll={false}
        tabIndex={tabIndex}
        data-depth={dataDepth}
        data-name={dataName}
        data-heading={dataHeading}
        aria-label={ariaLabel}
        {...{ [MXP_DESTINATION_ATTR]: localizedHref }}
        onClickCapture={handleClickCapture}
        onClick={onClickHandler}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {store && (
          <StoreBadge
            store={store}
            size={primary && size !== "small" ? "cta" : undefined}
          />
        )}
        {!store ? (children ? children : label) : null}
        {!store ? isIcon && <Icon type="t2u-chevron-right" size={20} /> : null}
      </Link>
    )
  } else {
    return (
      <a
        className={generateLinkStyles()}
        // disabled={disabled}
        href={destination}
        tabIndex={tabIndex}
        data-depth={dataDepth}
        data-name={dataName}
        data-heading={dataHeading}
        aria-label={ariaLabel}
        download={downloadName}
        target={
          downloadName ||
          (isExternalLink(destination) && !openInSameTabExternalLink)
            ? "_blank"
            : "_self"
        }
        rel={
          downloadName ||
          (isExternalLink(destination) && !openInSameTabExternalLink)
            ? "noopener noreferrer"
            : undefined
        }
        onClick={onClickHandler}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {store && (
          <StoreBadge
            store={store}
            size={primary && size !== "small" ? "cta" : undefined}
          />
        )}
        {!store ? (children ? children : label) : null}
        {!store ? isIcon && <Icon type="t2u-chevron-right" size={20} /> : null}
      </a>
    )
  }
}
